import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  features: string[];
}

export interface SubscriptionStatus {
  isActive: boolean;
  isTrial: boolean;
  isExpired: boolean;
  isBlocked: boolean;
  isSuperAdmin: boolean;
  daysRemaining: number | null;
  showWarning: boolean;
  warningLevel: 'info' | 'warning' | 'critical' | 'expired' | null;
  warningDays: number | null;
  endDate: string | null;
  billingCycle: string | null;
  plan: Plan | null;
  wasTrial: boolean;
}

interface SubscriptionContextType extends SubscriptionStatus {
  hasAccess: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface CheckSubscriptionResponse {
  subscribed: boolean;
  is_super_admin: boolean;
  status: string;
  access_status: string;
  plan: Plan | null;
  end_date: string | null;
  billing_cycle: string | null;
  days_remaining: number | null;
  show_warning: boolean;
  warning_level: string | null;
  warning_days: number | null;
  is_trial?: boolean;
  was_trial?: boolean;
}

const defaultStatus: SubscriptionStatus = {
  isActive: false,
  isTrial: false,
  isExpired: false,
  isBlocked: false,
  isSuperAdmin: false,
  daysRemaining: null,
  showWarning: false,
  warningLevel: null,
  warningDays: null,
  endDate: null,
  billingCycle: null,
  plan: null,
  wasTrial: false,
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>(defaultStatus);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const hasCheckedRef = useRef(false);

  const forceLogout = useCallback(async () => {
    try {
      // Prefer local sign out to guarantee local cleanup even if the server session is already gone
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // ignore
    }

    try {
      // Extra safety: clear any cached auth tokens
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // ignore
    }

    window.location.href = '/auth?logout=true';
  }, []);

  const checkSubscription = useCallback(async (isRetry = false) => {
    if (!user) {
      setStatus(defaultStatus);
      setLoading(false);
      hasCheckedRef.current = false;
      return;
    }

    // If already checked and not a manual refresh, skip
    if (hasCheckedRef.current && !isRetry) {
      return;
    }

    try {
      setError(null);

      // Always get fresh session before calling the function
      const { data: sessionData } = await supabase.auth.getSession();
      const currentSession = sessionData?.session;

      if (!currentSession) {
        await forceLogout();
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke<CheckSubscriptionResponse>('check-subscription', {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      // Handle 401/Invalid JWT with retry
      if (fnError) {
        const errorMessage = fnError.message || '';
        const lower = errorMessage.toLowerCase();
        const isAuthError =
          errorMessage.includes('401') ||
          lower.includes('invalid jwt') ||
          lower.includes('unauthorized') ||
          lower.includes('token') ||
          lower.includes('session');

        if (isAuthError) {
          if (!isRetry && retryCountRef.current < 1) {
            retryCountRef.current += 1;

            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError || !refreshData.session) {
              await forceLogout();
              return;
            }

            // Retry with the NEW session (not the old captured one)
            return checkSubscription(true);
          }

          await forceLogout();
          return;
        }

        throw new Error(fnError.message);
      }

      // Reset retry count on success
      retryCountRef.current = 0;
      hasCheckedRef.current = true;

      if (data) {
        const newStatus: SubscriptionStatus = {
          isActive: data.subscribed && data.access_status === 'active',
          isTrial: data.access_status === 'trial',
          isExpired: data.access_status === 'blocked' || data.warning_level === 'expired',
          isBlocked: data.access_status === 'blocked',
          isSuperAdmin: data.is_super_admin,
          daysRemaining: data.days_remaining,
          showWarning: data.show_warning,
          warningLevel: data.warning_level as SubscriptionStatus['warningLevel'],
          warningDays: data.warning_days,
          endDate: data.end_date,
          billingCycle: data.billing_cycle,
          plan: data.plan,
          wasTrial: data.was_trial || data.is_trial || false,
        };
        setStatus(newStatus);
      }
    } catch (err) {
      // Silently handle errors to reduce console noise
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [user, forceLogout]);

  // Check on mount and when user changes
  useEffect(() => {
    retryCountRef.current = 0;
    hasCheckedRef.current = false;
    setLoading(true);
    checkSubscription();
  }, [user?.id]); // Only re-check when user ID changes

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      hasCheckedRef.current = false; // Allow refresh
      checkSubscription();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const hasAccess = status.isActive || status.isTrial || status.isSuperAdmin;

  const refresh = useCallback(async () => {
    hasCheckedRef.current = false;
    await checkSubscription();
  }, [checkSubscription]);

  return (
    <SubscriptionContext.Provider value={{
      ...status,
      hasAccess,
      loading,
      error,
      refresh,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  
  // Retornar valores padrão se o contexto não estiver disponível
  // Isso evita crashes durante o logout ou navegação
  if (context === undefined) {
    return {
      ...defaultStatus,
      hasAccess: false,
      loading: true,
      error: null,
      refresh: async () => {},
    };
  }
  return context;
}
